import { type IncidentItemDto, IncidentStatus } from '@api/incidents';
import { IncidentColumn, incidentColumnTitle, type NormalizedIncident } from '@models';
import { type DatagridColumn } from '@ui';

import { normalizeIncidentsV2Grid } from './normalize-incidents-v2-grid';

const NETWORK_PORT_DEVICE_TYPE = 'Network Port';

export const staticIncidentV2ColumnTitle: Record<string, string> = {
  [IncidentColumn.INDEX]: '№',
  [IncidentColumn.TICKET_NUMBER]: 'Ticket №',
};

export type IncidentsV2GridRow = NormalizedIncident & {
  deviceLink: string[];
  isNetworkPort: boolean;
  notesIcon: string;
  parentId: string;
  parentName: string;
};

type NormalizedIncidentWithParent = NormalizedIncident & Pick<IncidentsV2GridRow, 'parentId' | 'parentName'>;

/* `normalizeIncidentsV2Grid` emits parentId/parentName but `NormalizedIncident` doesn't declare them. */
export function toIncidentsV2GridRows(incidents: IncidentItemDto[]) {
  return (normalizeIncidentsV2Grid(incidents) as NormalizedIncidentWithParent[]).map(row => {
    const isNetworkPort = row.deviceType === NETWORK_PORT_DEVICE_TYPE;
    const gridRow: IncidentsV2GridRow = {
      ...row,
      deviceLink: ['/devices', row.buildingId, isNetworkPort ? row.parentId || row.deviceId : row.deviceId],
      isNetworkPort,
      notesIcon: row.status === IncidentStatus.NEW ? 'add' : 'chat_bubble_outline',
    };

    return gridRow;
  });
}

const titledColumn = (id: IncidentColumn, sortable: boolean) => ({
  id,
  sortable,
  title: incidentColumnTitle[id],
});

export function buildIncidentsV2GridColumns(isMobile: boolean) {
  const leftStickyClass = isMobile ? 'table-border-last-left-sticky' : 'table-border-last-left-sticky left-10';
  const rightStickyClass = isMobile ? 'table-border-last-right-sticky' : 'table-border-last-right-sticky sticky-end';
  const columns: DatagridColumn<IncidentsV2GridRow>[] = [
    {
      id: IncidentColumn.INDEX,
      resizable: false,
      sticky: 'start',
      title: staticIncidentV2ColumnTitle[IncidentColumn.INDEX],
    },
    {
      cellClass: leftStickyClass,
      headerClass: leftStickyClass,
      id: IncidentColumn.TICKET_NUMBER,
      sortable: true,
      sticky: 'start',
      title: staticIncidentV2ColumnTitle[IncidentColumn.TICKET_NUMBER],
    },
    titledColumn(IncidentColumn.STATUS, true),
    titledColumn(IncidentColumn.DEVICE_ID, true),
    titledColumn(IncidentColumn.DEVICE_TYPE, true),
    titledColumn(IncidentColumn.BUILDING, true),
    titledColumn(IncidentColumn.FLOOR, false),
    titledColumn(IncidentColumn.ROOM, false),
    titledColumn(IncidentColumn.MANUFACTURER, true),
    titledColumn(IncidentColumn.MODEL, true),
    titledColumn(IncidentColumn.SERIAL_NUMBER, false),
    titledColumn(IncidentColumn.REPORTED, true),
    titledColumn(IncidentColumn.INCIDENT_RULE, true),
    titledColumn(IncidentColumn.RESOLVED, true),
    {
      cellClass: `px-3 text-center ${rightStickyClass}`,
      headerClass: rightStickyClass,
      id: IncidentColumn.COMMENTS,
      sticky: 'end',
      title: '',
    },
  ];

  return columns;
}
